import {styled, css} from "@mui/material/styles"
import {Typography} from "@mui/material"

import {colors} from "./colors"

const Weight = {
	Regular: 400,
	Medium: 500,
	SemiBold: 600,
	Bold: 700,
	ExtraBold: 800,
} as const

type SizeConfig = {fontSize: string; lineHeight: string; letterSpacing?: string; responsiveFontSize?: string}

const Size = {
	ExtraSmall: {fontSize: "0.75rem", lineHeight: "1.125rem"},
	Small: {fontSize: "0.875rem", lineHeight: "1.25rem"},
	Medium: {fontSize: "1rem", lineHeight: "1.5rem"},
	ExtraLarge: {fontSize: "1.25rem", lineHeight: "1.875rem"},
	Large: {fontSize: "1.125rem", lineHeight: "1.75rem"},
} as const satisfies Record<string, SizeConfig>

const DisplaySize = {
	ExtraExtraLarge: {fontSize: "4.5rem", lineHeight: "5.625rem", letterSpacing: "-0.06rem"},
	ExtraLarge: {fontSize: "3.75rem", lineHeight: "4.5rem", letterSpacing: "-0.06rem"},
	Large: {fontSize: "3rem", lineHeight: "3.75rem", letterSpacing: "-0.06rem"},
	Medium: {fontSize: "2.25rem", lineHeight: "2.75rem", letterSpacing: "-0.06rem"},
	Small: {fontSize: "1.875rem", lineHeight: "2.375rem"},
	ExtraSmall: {fontSize: "1.5rem", lineHeight: "2rem"},
	ExtraSmallResponsive: {fontSize: "1.5rem", lineHeight: "2rem", responsiveFontSize: "1.875rem"},
} as const satisfies Record<string, SizeConfig>

function makeVariants(size: SizeConfig) {
	const baseCss = (fontWeight: number) => css`
		font-weight: ${fontWeight};
		font-size: ${size.fontSize};
		line-height: ${size.lineHeight};
		${size.letterSpacing ? `letter-spacing: ${size.letterSpacing};` : ""}
		${size.responsiveFontSize ? `@media (min-width: 600px) { font-size: ${size.responsiveFontSize}; }` : ""}
	`

	const base = (fontWeight: number) => {
		const component = styled(Typography)`
			${baseCss(fontWeight)}
			color: ${(props) => props.color || colors.endlessGalaxy["900"]};
		`
		return Object.assign(component, {css: baseCss(fontWeight)})
	}

	return Object.fromEntries(Object.entries(Weight).map(([name, fontWeight]) => [name, base(fontWeight)])) as {
		[K in keyof typeof Weight]: ReturnType<typeof base>
	}
}

export const Text = Object.fromEntries(Object.entries(Size).map(([key, value]) => [key, makeVariants(value)])) as {
	[K in keyof typeof Size]: ReturnType<typeof makeVariants>
}

export const Display = Object.fromEntries(
	Object.entries(DisplaySize).map(([key, value]) => [key, makeVariants(value)]),
) as {
	[K in keyof typeof DisplaySize]: ReturnType<typeof makeVariants>
}
